/**
 * StatCard Component - Phase 2C-1
 * 
 * Single statistic display with label and optional subtitle.
 * All stat values use monospace font for data clarity.
 * 
 * BSL Compliance: No red/green sentiment colors.
 */

import React from 'react';
import { DataValue, DataValueFormat, DataValueSize } from './DataValue';

export interface StatCardProps {
  label: string;
  value: number | string | null | undefined;
  format?: DataValueFormat;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label?: string;
  };
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  className?: string;
}

const sizeConfig = {
  sm: {
    padding: 'p-4',
    labelSize: 'text-xs',
    valueSize: 'lg' as DataValueSize,
    subtitleSize: 'text-xs',
    iconSize: 'w-8 h-8',
  },
  md: {
    padding: 'p-5',
    labelSize: 'text-xs',
    valueSize: 'xl' as DataValueSize,
    subtitleSize: 'text-sm',
    iconSize: 'w-10 h-10',
  },
  lg: {
    padding: 'p-6',
    labelSize: 'text-sm',
    valueSize: '2xl' as DataValueSize,
    subtitleSize: 'text-sm',
    iconSize: 'w-12 h-12',
  },
};

export function StatCard({
  label,
  value,
  format = 'number',
  subtitle,
  icon,
  trend,
  size = 'md',
  loading = false,
  className = '',
}: StatCardProps): JSX.Element {
  const config = sizeConfig[size];
  
  if (loading) {
    return (
      <div
        className={`
          bg-slate-800 border border-slate-700 rounded-lg
          ${config.padding}
          ${className}
        `.trim()}
      >
        <div className="animate-pulse">
          <div className="h-3 w-20 bg-slate-700 rounded mb-2" />
          <div className="h-8 w-24 bg-slate-700 rounded mb-1" />
          {subtitle && <div className="h-3 w-16 bg-slate-700 rounded" />}
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={`
        bg-slate-800 border border-slate-700 rounded-lg
        ${config.padding}
        ${className}
      `.trim()}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Label */}
          <p
            className={`
              font-medium text-slate-400 uppercase tracking-wider mb-1
              ${config.labelSize}
            `.trim()}
          >
            {label}
          </p>
          
          {/* Value */}
          <div className="flex items-baseline gap-2">
            <DataValue
              value={value}
              format={format}
              size={config.valueSize}
              className="text-slate-100"
            />
            
            {/* Trend indicator - BSL: neutral colors */}
            {trend && (
              <span
                className={`
                  text-xs font-medium
                  ${trend.value >= 0 ? 'text-blue-400' : 'text-slate-400'}
                `}
              >
                {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}
                {trend.label && ` ${trend.label}`}
              </span>
            )}
          </div>
          
          {/* Subtitle */}
          {subtitle && (
            <p className={`text-slate-500 mt-1 ${config.subtitleSize}`}>
              {subtitle}
            </p>
          )}
        </div>
        
        {/* Icon */}
        {icon && (
          <div className={`${config.iconSize} text-slate-600 flex-shrink-0`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StatCardGrid - Grid container for multiple stat cards
 */
export interface StatCardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function StatCardGrid({
  children,
  columns = 4,
  className = '',
}: StatCardGridProps): JSX.Element {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };
  
  return (
    <div className={`grid gap-4 ${gridCols[columns]} ${className}`}>
      {children}
    </div>
  );
}

/**
 * StatRow - Horizontal stat display (inline)
 */
export interface StatRowProps {
  label: string;
  value: number | string | null | undefined;
  format?: DataValueFormat;
  className?: string;
}

export function StatRow({
  label,
  value,
  format = 'number',
  className = '',
}: StatRowProps): JSX.Element {
  return (
    <div className={`flex items-center justify-between py-2 ${className}`}>
      <span className="text-sm text-slate-400">{label}</span>
      <DataValue value={value} format={format} size="sm" />
    </div>
  );
}

/**
 * StatList - Vertical list of stats
 */
export interface StatListProps {
  stats: Array<{
    label: string;
    value: number | string | null | undefined;
    format?: DataValueFormat;
  }>;
  className?: string;
  divided?: boolean;
}

export function StatList({
  stats,
  className = '',
  divided = true,
}: StatListProps): JSX.Element {
  return (
    <div className={className}>
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={divided && index > 0 ? 'border-t border-slate-700' : ''}
        >
          <StatRow label={stat.label} value={stat.value} format={stat.format} />
        </div>
      ))}
    </div>
  );
}

/**
 * Preset Icons for StatCard
 */
export const StatIcons = {
  balance: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  ),
  predictions: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  winRate: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
    </svg>
  ),
  pools: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  ),
};

export default StatCard;
